import { Box, Theme, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useAuth } from 'components/AuthProvider'
import DefaultLayout from 'components/layout/DefaultLayout'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import {
  BlinkPayTransferStatusPath,
  walletPath
} from 'components/Routes/Routes'
import { useMutateVerifyBlinkpayTransaction } from 'hooks/useTransfers'
import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontSize: '3rem',
    margin: theme.spacing(4, 0)
  },
  loading: {
    margin: 'auto',
    color: theme.palette.primary.main
  }
}))

export default function BlinkPayTransferLoading(): JSX.Element {
  const { classes } = useStyles()
  const { setToast } = useAuth()
  const navigate = useNavigate()
  const { search } = useLocation()
  const quickPaymentId = new URLSearchParams(search).get('cid')
  const hasRequested = useRef(false)

  const { mutate: verifyBlinkpayTransaction, isLoading } =
    useMutateVerifyBlinkpayTransaction()

  // Both stranding branches below must do this: this page renders as a
  // full-viewport overlay with no content of its own, so ending here without
  // navigating leaves the user on a blank screen once the toast dismisses. The
  // stored redirect keys (set by TransactionButtons on the way OUT to BlinkPay)
  // must also go, or the abandoned payment's redirect gets consumed by a LATER
  // BlinkPay visit.
  const abandonToWallet = () => {
    localStorage.removeItem('url_after_blinkpay_payment')
    localStorage.removeItem('blinkpay_review_order_id')
    navigate(walletPath(), { replace: true })
  }

  useEffect(() => {
    if (!quickPaymentId) {
      setToast({
        message: 'Missing payment session. Please start the payment again.',
        open: true,
        duration: 3000,
        type: 'error'
      })
      abandonToWallet()
      return
    }
    if (hasRequested.current) return
    hasRequested.current = true

    verifyBlinkpayTransaction(quickPaymentId, {
      onSuccess: data => {
        navigate(BlinkPayTransferStatusPath(), {
          state: {
            status: data.transactionStatus,
            success: data.success,
            message: data.message,
            purchaseExecuted: data.purchaseExecuted
          }
        })
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'An error occurred'
        setToast({
          message,
          open: true,
          duration: 3000,
          type: 'error'
        })
        abandonToWallet()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickPaymentId])

  return (
    <DefaultLayout
      wrapperContainer={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <Box>
        <Typography variant='h1' component='h1' className={classes.header}>
          BLINKPAY PAYMENT LOADING
        </Typography>
        <LoadingWrapper className={classes.loading} loading={isLoading}>
          <div />
        </LoadingWrapper>
      </Box>
    </DefaultLayout>
  )
}
